import React, { useContext, useMemo, useState, useEffect, useRef } from 'react';
import { UserContext } from '../../context/UserContext';
import { useMapContext } from '../../context/MapContext';
import { ConfigContext } from '../../context/ConfigContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../hooks/useLanguage';
// Import useLocation, useParams along with other router hooks
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './TopBar.css';
import { convertCreditsToTargetCurrency, formatCurrency } from '../../utils/priceCalculator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins } from '@fortawesome/free-solid-svg-icons';
import logo from '../../assets/images/logo.png'; // Import the logo
import UserMenu from './UserMenu';
import enFlag from '../../assets/flags/en.svg';
import Cookies from 'js-cookie';
import heFlag from '../../assets/flags/he.svg';
import ruFlag from '../../assets/flags/ru.svg';
import esFlag from '../../assets/flags/es.svg';
import FilterControls from './FilterControls';
import MobileFilterDrawer from './MobileFilterDrawer';
import CartIcon from '../payment/CartIcon';
import ActiveUserCount from './ActiveUserCount';
import { IS_PRERENDERING } from '../../utils/config';

// Define the path where your MapComponent is rendered
const MAP_PAGE_PATH = '/map';
const PROPERTIES_PAGE_PATH = '/properties';

const languageOptions = {
  en: { name: 'English', flag: enFlag },
  he: { name: 'עברית', flag: heFlag },
  ru: { name: 'Русский', flag: ruFlag },
  es: { name: 'Español', flag: esFlag },
};

const TopBar = ({ activeUsers, error }) => {
  const { user } = useContext(UserContext);
  const { filters, selectMapLocation } = useMapContext();
  const { config } = useContext(ConfigContext);
  const { t } = useTranslation();
  const lang = useLanguage();
  const availablePoints = user.creditPoints - (user.pointsOnHold || 0);

  // Get navigation and location hooks
  const navigate = useNavigate();
  const location = useLocation(); // Get current location object
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 1200);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const filterControlsRef = useRef(null);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  // Determine if map-related controls should be shown based on the current page
  const showMapControls = location.pathname.endsWith(MAP_PAGE_PATH);
  const showPropertiesControls = location.pathname.endsWith(PROPERTIES_PAGE_PATH);

  const isAnyFilterActive = useMemo(() => {
    const currentParams = new URLSearchParams(location.search);
    return (
      currentParams.get('view') === 'my' ||
      currentParams.get('show') === 'saved' ||
      filters.state ||
      filters.price?.$gte ||
      filters.price?.$lte ||
      filters.beds?.$gte ||
      filters.beds?.$lte ||
      (filters.type && filters.type.length > 0) ||
      filters.yearBuilt?.$gte ||
      filters.yearBuilt?.$lte ||
      (filters.status && filters.status.length > 0) ||
      filters.propertyID ||
      filters.sellerUsername
    );
  }, [location.search, filters]);

  // --- LOGGING: Inspect the user object from context ---
  // useEffect(() => {
  //   console.log('[TopBar] User object from context:', { creditPoints: user?.creditPoints, pointsOnHold: user?.pointsOnHold });
  // }, [user]);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 1200);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setIsLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClearMobileFilters = () => {
    if (filterControlsRef.current) {
      filterControlsRef.current.clear();
    }
  };

  // Calculate the value of credit points in the user's preferred currency
  const creditsValueDisplay = useMemo(() => {
    if (!user.isLoggedIn || !config) return '';
    const userCurrency = user?.preferences?.currency || 'ILS';
    const rates = {
      usdToIlsRate: config?.usdToIlsRate,
      eurToIlsRate: config?.eurToIlsRate,
      creditPointValueILS: config?.creditPointValueILS,
    };
    const creditsValueInCurrency = convertCreditsToTargetCurrency(availablePoints, userCurrency, rates);
    return formatCurrency(creditsValueInCurrency, userCurrency);
  }, [availablePoints, user.isLoggedIn, user.preferences?.currency, config]);

  // --- MODIFIED: Create a more detailed tooltip for credit points ---
  const creditPointsTitle = useMemo(() => {
    if (!user.isLoggedIn) return '';
    return `Total Credits: ${user.creditPoints}\nOn Hold: ${user.pointsOnHold || 0}\nAvailable for Purchases: ${availablePoints} (≈${creditsValueDisplay})`;
  }, [user.creditPoints, user.pointsOnHold, availablePoints, creditsValueDisplay, user.isLoggedIn]);
  // --- END MODIFICATION ---

  // --- MODIFIED: Handle location selection and navigation ---
  const handleLocationSelectInternal = (selectedAddress) => {
    // 1. Update the context immediately.
    //    MapPage will pick this up when it mounts or updates.
    selectMapLocation(selectedAddress);

    // 2. Check if navigation is needed.
    const currentPath = location.pathname; // Get the current path

    if (currentPath !== MAP_PAGE_PATH) {
      // Navigate to the map page if not already there
      navigate(MAP_PAGE_PATH);
    } else {
      // No navigation needed, MapPage's useEffect watching selectedLocation will handle centering/zoom.
    }

    // Note: Removed closeMenu() here as the search is not inside the menu itself.
  };
  // --- END MODIFICATION ---

  return (
    <div className="top-bar">
      <div className="top-bar-left-cluster">
        <Link to={`/${lang}`} className="top-bar-logo-link">
          <img src={logo} alt="SeegSoog Logo" className="top-bar-logo" />
          {!IS_PRERENDERING && user.isLoggedIn && user.creditPoints > 0 && (
            <div className="credit-points-display" title={creditPointsTitle}>
              <FontAwesomeIcon icon={faCoins} className="credit-points-icon" />
              <span>{user.creditPoints}</span>
            </div>
          )}
        </Link>
        {!IS_PRERENDERING && user.isAdmin && activeUsers && <ActiveUserCount activeUsers={activeUsers} error={error} />}

      </div>

      {(showMapControls || showPropertiesControls) &&
        (isMobileView ? (
          <>
            <button className={`mobile-filters-trigger ${isAnyFilterActive ? 'applied' : ''}`} onClick={() => setIsMobileDrawerOpen(true)}>
              Filters
              {isAnyFilterActive && <span className="filter-active-dot"></span>}
            </button>
            <MobileFilterDrawer
              isOpen={isMobileDrawerOpen}
              onClose={() => setIsMobileDrawerOpen(false)}
              onApply={() => setIsMobileDrawerOpen(false)}
              onClear={handleClearMobileFilters}
              isAnyFilterActive={isAnyFilterActive}
              filterControls={<FilterControls ref={filterControlsRef} onLocationSelect={handleLocationSelectInternal} layout="vertical" />}
            />
          </>
        ) : (
          <div className="top-bar-controls-wrapper">
            <FilterControls onLocationSelect={handleLocationSelectInternal} layout="horizontal" />
          </div>
        ))}

      <div className="top-bar-right-cluster">
        <div className="top-bar-legal-links">
          <Link to={`/${lang}/privacy-policy`} className="top-bar-legal-link">
            {t('topBar.privacyPolicy')}
          </Link>
        </div>
        <div className="top-bar-user-actions">
          <div className="language-switcher" ref={langMenuRef}>
            <button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className="language-switcher-button" aria-label={t('language_switcher_label')} title={languageOptions[lang]?.name}>
              <img src={languageOptions[lang]?.flag} alt={languageOptions[lang]?.name} className="language-flag-icon" />
            </button>
            {isLangMenuOpen && (
              <div className="language-switcher-dropdown">
                <ul>
                  {Object.keys(languageOptions).map((langCode) => (
                    <li
                      key={langCode}
                      onClick={() => {
                        Cookies.set('site_language', langCode, { expires: 365 });
                        // Replace the old language code in the path with the new one
                        const newPath = `/${langCode}${location.pathname.substring(lang.length + 1)}`;
                        navigate(newPath);
                        setIsLangMenuOpen(false);
                      }}
                    >
                      <img src={languageOptions[langCode].flag} alt={languageOptions[langCode].name} className="language-flag-icon" />
                      <span>{languageOptions[langCode].name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <UserMenu />
          {!IS_PRERENDERING && user.isLoggedIn && <CartIcon />}
        </div>
      </div>
    </div>
  );
};

TopBar.defaultProps = {
  activeUsers: null,
  error: null,
};

export default TopBar;
